// src/components/Dashboard/DashboardConfigure.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { HbrInput, HbrSwitch } from '@harbor/elements-react-standalone';
import { useFetchCustomTileCategories, useFetchCustomTiles } from '../../../hooks/toolbox-hooks';
import { Dashboard, CustomTileCategory, RegistryTile, CustomTile } from '../../../types/dashboard';
import DashboardList from './DashboardList';
import StbButton from '../../security-toolbox-ui/StbButton';
import StbModal from '../../security-toolbox-ui/StbModal';
import { useUserRegistry } from '../../../hooks/useUserRegistry';
import StbMultiSelectGroupedWithSearch from '../../security-toolbox-ui/StbMultiSelectGroupedWithSearch';


interface TileOption {
  id: string;
  name: string;
  category: string;
}

interface DashboardConfigureModalProps {
  dashboards: Record<Dashboard['id'], Dashboard>;
  selected: Dashboard['id'];
  setSelected: React.Dispatch<React.SetStateAction<Dashboard['id'] | null>>;
}

const DashboardConfigureModal: React.FC<DashboardConfigureModalProps> = ({
  dashboards,
  selected,
  setSelected
}) => {
  const {
    updateUserDashboard
  } = useUserRegistry();

  const { data: customTileData } = useFetchCustomTiles();
  const { data: categoryData, isLoading: categoryDataLoading } = useFetchCustomTileCategories();

  const categories = useMemo(() => {
    if (categoryDataLoading || !categoryData) return {};
    return categoryData.reduce((acc, category) => {
      acc[category.id] = category;
      return acc;
    }, {} as Record<CustomTileCategory['id'], CustomTileCategory> );
  }, [categoryData, categoryDataLoading]);

  const [isRssVisible, setIsRssVisible] = useState<boolean>(false);
  const [editingName, setEditingName] = useState<boolean>(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [name, setName] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tileDataLoading, setTileDataLoading] = useState<boolean>(!customTileData || Object.keys(categories).length === 0);
  const [addDashboardModalOpen, setAddDashboardModalOpen] = useState(false);
  
  useEffect(() => {
    setSelected(selected ?? null);
    setIsRssVisible((selected && dashboards[selected]) ? dashboards[selected].isRssVisible : false);
    setEditingName(false);
    setNameTouched(false);
    setName('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const [selectedTiles, setSelectedTiles] = useState<TileOption[]>([]);
  useEffect(() => {
    if (!categories || !selected || !dashboards || !dashboards[selected] || !customTileData) {
      setSelectedTiles([]);
      return;
    }

    const tiles: TileOption[] = (dashboards[selected].tiles || []).map((tile) => {
      const customTile = customTileData.find((t) => Number(t.id) === Number(tile.id));
      return {
        name: customTile?.name,
        id: String(tile?.id),
        category: categories[Number(customTile?.category)]?.name
      }
    });

    setSelectedTiles(tiles);
  }, [selected, dashboards, categories, customTileData]);

  // set of all selectable tiles for searchable grouped multi-select
  const availableTiles = useMemo<TileOption[]>(() => {
    if (!customTileData || Object.keys(categories).length === 0) return [];
    const result = customTileData
      .map((tile) => {
        const category = categories[Number(tile.category)];
        return {
          name: tile.name,
          id: String(tile.id),
          category: category?.name,
        };
      }
    );
    setTileDataLoading(false);
    return result;
  }, [customTileData, categories]);

  const handleSelectDashboard = (dashboardId: string) => setSelected(dashboardId);

  const handleAddDashboard = async () => {
    setSaving(true);
    const newDashboardId = uuidv4();
    const newDashboard: Dashboard = {
      id: newDashboardId,
      name: name.trim(),
      tiles: [],
      isRssVisible: false,
    };

    await updateUserDashboard({ ...dashboards, [newDashboardId]: newDashboard });
    setSelected(newDashboard.id);
    setIsRssVisible(false);
  };

  const handleUpdateTiles = async (selectedTiles: TileOption[]) => {
    setSelectedTiles([...selectedTiles]);

    if (!selected) return;

    const tiles: RegistryTile[] = dashboards[selected]?.tiles || [];

    const selectedIds = new Set(selectedTiles.map((t) => Number(t.id)));
    const currentIds = new Set(tiles.map((t) => Number(t.id)));

    const toAdd = [...selectedIds].filter((id) => !currentIds.has(id));
    const toRemove = [...currentIds].filter((id) => !selectedIds.has(id));

    // Nothing to do
    if (toAdd.length === 0 && toRemove.length === 0) return;

    // Compute bottom-most y so new tiles are appended below existing ones
    const getBottomY = (ts: RegistryTile[]) =>
      ts.reduce((max, t) => {
        const l = t.layout;
        if (!l) return max;
        const bottom = (Number(l.y) || 0) + (Number(l.h) || 0);
        return Math.max(max, bottom);
      }, 0);

    const DEFAULT_W = 2;
    const DEFAULT_H = 6;

    let updatedTiles: RegistryTile[] = tiles;

    // Remove first (so bottom calculation reflects current final set if you want)
    if (toRemove.length > 0) {
      updatedTiles = updatedTiles.filter((t) => !toRemove.includes(Number(t.id)));
    }

    if (toAdd.length > 0) {
      const templates: CustomTile[] = (customTileData || []).filter((t) => toAdd.includes(Number(t.id)));

      let nextY = getBottomY(updatedTiles);

      const newTiles: RegistryTile[] = templates.map((def) => {
        const rt: RegistryTile = {
          id: Number(def.id),
          chart_type: def.chart_type,
          name: def.name,
          customTile: true,
          layout: {
            x: 0,
            y: nextY,
            w: DEFAULT_W,
            h: DEFAULT_H,
            i: String(def.id),
            moved: false,
            static: false,
          },
        } as RegistryTile;

        // stack subsequent new tiles under the previous one
        nextY += DEFAULT_H;
        return rt;
      });

      updatedTiles = [...updatedTiles, ...newTiles];
    }

    await updateUserDashboard({
      ...dashboards,
      [selected]: {
        ...dashboards[selected],
        tiles: updatedTiles,
      },
    });
  };

  const handleToggleRssVisible = async () => {
    if (!selected) return;

    const visible = isRssVisible;
    setIsRssVisible(!visible)
    await updateUserDashboard({
        ...dashboards,
        [selected]: {
          ...dashboards[selected],
          isRssVisible: !visible
        }
      });
  };

  const handleSaveNameUpdate = async (e) => {
    e.stopPropagation();
    
    if (name.trim()) {
      await updateUserDashboard({
        ...dashboards,
        [selected]: {
          ...dashboards[selected],
          name: name.trim()
        }
      });
    }
    setEditingName(false);
    setNameTouched(false);
    setName('');
    setSaving(false);
  }

  const handleSaveNameCancel = (e) => {
    e.stopPropagation();
    setName('');
    setEditingName(false);
    setNameTouched(false);
  };

  const handleClickAdd = () => {
    setEditingName(false);
    setName('');
    setNameTouched(false);
    setAddDashboardModalOpen(true)
  };

  const generateNameError = (name: string): string | null => {
    if (saving) return null;

    const trimmedName = name.trim();
    if (trimmedName === '') {
      return 'Dashboard name is required';
    }

    const exists = Object.values(dashboards).some(
      (d) => d.name === trimmedName
    );
    return exists ? 'A dashboard with this name already exists' : null;
  };

  return (
    <div className="flex flex-row gap-6">
      <aside className="hbr-css__layout-col-sm" style={{
        width: '16rem',
        flexShrink: 0,
        backgroundColor: 'var(--hbr-color-neutral-bg-medium)',
        marginTop: 'calc(-2.75em - 3px)',
        paddingTop: 'calc(2.75em + 3px)',
        marginLeft: '-1.5em',
        paddingLeft: '1.5em',
        borderTopLeftRadius: '.375rem',
        borderBottomLeftRadius: '.375rem',
      }}>
        <div className="hbr-css__text-heading-lg-bold flex flex-row items-center justify-between mr-4">
          <div>Dashboards</div>
          <StbButton
            role="add"
            variant="text"
            onClick={() => handleClickAdd()}
          />
        </div>
        <DashboardList
          dashboards={dashboards}
          selectedId={selected || null}
          onSelectListItem={handleSelectDashboard}
        />
      </aside>

      <main className="flex-1 min-w-0">
        <div>
          {editingName
            ? (
                <div className="flex flex-row gap-4">
                  <HbrInput
                    value={name}
                    onHbr-input={(e) => {
                      setName(e.target.value);
                      setNameTouched(true);
                      setError(generateNameError(e.target.value));
                    }}
                    error={!!(nameTouched && error)}
                    placeholder="Dashboard Name"
                  >
                    {nameTouched && error && 
                      <div slot="help-text" className='text-sm mt-2 text-danger'>{error}</div>
                    }
                  </HbrInput>
                  <div className="flex flex-row gap-2 items-center">
                    <StbButton
                      role="save"
                      variant="text"
                      iconOnly
                      onClick={(e) => {
                        setSaving(true);
                        handleSaveNameUpdate(e);
                      }}
                      disabled={saving || name === dashboards[selected]?.name || (name ?? '').trim() === '' || error !== null}
                    />
                    <StbButton
                      role="cancel"
                      variant="text"
                      iconOnly
                      onClick={handleSaveNameCancel}
                    />
                  </div>
                </div>
              )
            : (
                <div className="hbr-css__text-heading-lg-bold flex flex-row gap-4 items-center">
                  {dashboards[selected]?.name || 'Configure Dashboard'}
                  <StbButton
                    role="update"
                    variant="text"
                    iconOnly
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingName(true);
                      setName(dashboards[selected]?.name ?? '');
                    }}
                  />
                </div>
              )
          }
        </div>

        <div className="mt-4">
          <HbrSwitch checked={isRssVisible} onHbr-click={() => handleToggleRssVisible()}>
            {isRssVisible ? 'Hide Newsfeed' : 'Show Newsfeed'}
          </HbrSwitch>
        </div>
              
        <StbMultiSelectGroupedWithSearch
          items={availableTiles}
          selected={selectedTiles}
          groupBy={'category'}
          onChange={(selected) => handleUpdateTiles(selected as TileOption[])}
          className="w-full mt-4"
          inline={true}
          placeholder="Search tiles to add"
          loading={tileDataLoading}
        />
      </main>

      {/* Add Dashboard Modal */}
      <StbModal
        title="Add New Dashboard"
        role="save"
        open={addDashboardModalOpen}
        onClose={async () => {
          setNameTouched(false);
          setName('');
          setAddDashboardModalOpen(false)
          setSaving(false);
          setError(null);
        }}
        onSubmit={async () => handleAddDashboard()}
        submitDisabled={(name ?? '').trim() === '' || error !== null}
        submitLabel="Add Dashboard"
      >
        <HbrInput
          value={name}
          onHbr-input={(e) => {
            setName(e.target.value);
            setNameTouched(true);
            setError(generateNameError(e.target.value));
          }}
          error={!!(nameTouched && error)}
          placeholder="Dashboard Name"
        >
          <div slot="label">Dashboard Name <em className="text-gray-500">(required)</em></div>
          {nameTouched && error && 
            <div slot="help-text" className='text-sm mt-2 text-danger'>{error}</div>
          }
        </HbrInput>
      </StbModal>
    </div>
  );
};

export default DashboardConfigureModal;